import './counter-signature.scss'

import { defineComponent, PropType } from 'vue';
import { set } from 'lodash';
import { faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';

import { CounterSignatureResource, CounterSignerResource, SkinDate, createTranslatedString, getTranslatedString } from "@skinsoft/types";

import { reactiveVueMixin } from "@skinsoft/reactive-vue";
import { FIELD_VALUE_UPDATED } from "@skinsoft/ui2/events";
import { COUNTER_SIGNATURE_UPDATED, COUNTER_SIGNER_UPDATED, REMOVE_COUNTER_SIGNATURE, REMOVE_COUNTER_SIGNER } from "../../../../../busTopics";
import { DATE_FORMATS, buildSkinDateWithFormat, toFormat } from "@skinsoft/utils/date-helper";
import { TextField, TextArea } from '@skinsoft/ui2';
import { THESAURUS_TERM_SELECTED } from '@skinsoft/ui/src/thesaurus-term-tree/thesaurus-term-tree-modal';
import ThesaurusTermTree from '@skinsoft/ui/src/thesaurus-term-tree';
import { CounterSigner } from "./counter-signer";

export const CounterSignature = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sTextField: TextField,
        sTextArea: TextArea,
        sThesaurusTermTree: ThesaurusTermTree,
        sCounterSigner: CounterSigner,
    },
    props: {
        counterSignature: {
            type: Object as PropType<CounterSignatureResource>,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        canBeDeleted: {
            type: Boolean,
        },
        showSigners: {
            type: Boolean,
            default: true,
        }
    },
    template: require('./counter-signature.html'),
    data() {
        return {
            lastSignerId: 1,
            minusIcon: faTrash,
            plusIcon: faPlusCircle,
            COUNTER_SIGNER_UPDATED,
            REMOVE_COUNTER_SIGNER,
            FIELD_VALUE_UPDATED,
            THESAURUS_TERM_SELECTED,
        }
    },
    computed: {
        dateValue: {
            get(): string {
                if(this.counterSignature.date) {
                    return toFormat(this.counterSignature.date as SkinDate, DATE_FORMATS.YYYY_MM_DD_DASH)
                }
                return ""
            },
            set(value: string) {
                if(value) {
                    let updatedSigner: CounterSignatureResource = {...this.counterSignature}
                    updatedSigner.date = buildSkinDateWithFormat(value, DATE_FORMATS.YYYY_MM_DD_DASH)
                    this.updateCounterSignature(updatedSigner)
                }
            },
        },
    },
    methods: {
        onTranslatedStringUpdated(path: string, value: string): CounterSignatureResource {
            let updatedSignature: CounterSignatureResource = {...this.counterSignature}
            if(updatedSignature[path]?.translatedString) {
                updatedSignature[path].translatedString[this.getAppLocale()] = value
            } else {
                updatedSignature[path] = createTranslatedString(this.getAppLocale(), value)
            }
            return updatedSignature
        },

        getComment() {
            return getTranslatedString(this.counterSignature.comment, this.getAppLocale())
        },

        onCommentUpdated(comment: string) {
            let updatedSignature: CounterSignatureResource = this.onTranslatedStringUpdated('comment', comment)
            this.updateCounterSignature(updatedSignature)
        },

        getLocation() {
            return getTranslatedString(this.counterSignature.location, this.getAppLocale())
        },

        onLocationUpdated(location: string) {
            let updatedSignature: CounterSignatureResource = this.onTranslatedStringUpdated('location', location)
            this.updateCounterSignature(updatedSignature)
        },

        getReason() {
            return this.counterSignature.reason
        },

        onReasonUpdated(reason: string) {
            let updatedSignature: CounterSignatureResource = {...this.counterSignature}
            updatedSignature.reason = reason
            this.updateCounterSignature(updatedSignature)
        },

        onCounterSignerUpdated(updatedSigner: CounterSignerResource, index: number) {
            if(this.counterSignature.observationCarriedPresenceOfs?.length) {
                let updatedSignature = {...this.counterSignature}
                const signerIndex = updatedSignature.observationCarriedPresenceOfs!.findIndex(signer => {
                    return signer.index === index
                })
                if(signerIndex > -1) {
                    updatedSignature.observationCarriedPresenceOfs!.splice(signerIndex, 1, updatedSigner)
                    this.updateCounterSignature(updatedSignature)
                }
            }
        },

        onCounterSignerDeleted(index: number) {
            if(this.counterSignature.observationCarriedPresenceOfs?.length) {
                let updatedSignature = {...this.counterSignature}
                updatedSignature.observationCarriedPresenceOfs = updatedSignature.observationCarriedPresenceOfs!.filter(signer => {
                    return signer.index !== index
                })
                this.updateCounterSignature(updatedSignature)
            }
        },

        onAddSigner() {
            let updatedSignature: CounterSignatureResource = {...this.counterSignature}
            const blankSigner: CounterSignerResource = {
                index: this.lastSignerId,
                surnameFirstName: createTranslatedString(this.getAppLocale(), ""),
                function: undefined,
                signature: undefined,
            }
            this.lastSignerId++

            if(updatedSignature.observationCarriedPresenceOfs) {
                updatedSignature.observationCarriedPresenceOfs.push(blankSigner)
            } else {
                set(updatedSignature, 'observationCarriedPresenceOfs', [blankSigner])
            }
            this.updateCounterSignature(updatedSignature)
        },

        updateCounterSignature(updatedSignature: CounterSignatureResource) {
            this.$emit(
                COUNTER_SIGNATURE_UPDATED,
                updatedSignature,
            )
        },

        removeCounterSignature() {
            this.$emit(
                REMOVE_COUNTER_SIGNATURE,
                this.counterSignature.index
            )
        },

        getDateFormatOptions() {
            return {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }
        },
    },
})
